import React, { ReactNode, forwardRef, useImperativeHandle, CSSProperties } from 'react';
import {
  useForm,
  FormProvider,
  UseFormReturn,
  FieldValues,
  FieldError,
  SetValueConfig,
  UseFormProps,
} from 'react-hook-form';

interface FormProps {
  id?: string;
  onSubmit?: (data: FieldValues) => void;
  onError?: (errors: Record<string, FieldError>) => void;
  children?: ReactNode;
  width?: string;
  style?: CSSProperties;
  formOptions?: UseFormProps;
}

export interface FormRef<T extends FieldValues = FieldValues> extends UseFormReturn<T> {
  setValues: (values: FieldValues, options?: SetValueConfig) => void;
}

const Form = forwardRef<FormRef, FormProps>((props, ref) => {
  const {
    id,
    onSubmit = () => {},
    onError = () => {},
    children,
    width = '100%',
    style = {},
    formOptions = { mode: 'all' },
  } = props;
  const methods = useForm(formOptions);
  const { handleSubmit, setValue } = methods;

  const setValues = (values: FieldValues, options?: SetValueConfig) => {
    for (const [key, value] of Object.entries(values)) {
      setValue(key, value, options);
    }
  };

  useImperativeHandle(ref, () => ({ ...methods, setValues }));

  return (
    <FormProvider {...methods}>
      <form style={{ width, ...style }} id={id} onSubmit={handleSubmit(onSubmit, onError)}>
        {children}
      </form>
    </FormProvider>
  );
});

export default Form;
